import Grid from "@material-ui/core/Grid";
import { CommodityInput } from "components/inputs/CommodityInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { CountryISOCodeInput } from "components/inputs/CountryISOCodeInput";
import { HiddenInput } from "components/inputs/HiddenInput";
import { ExternalTerminalConnectionStatus } from "entities/ExternalTerminalConnectionStatus";
import React, { useCallback, useEffect, useState } from "react";
import {
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import { isInArray } from "utils/general";

import { addHoursToDate } from "../../../components/_helpers/dateTime";
import { formatPhoneNumber } from "../../../components/fields/PhoneField";
import { FormGroupGrid, GridForm, GridInput } from "../../../components/GridForm";
import { NumberInputEx } from "../../../components/inputs";
import { CompanySelect, DateInput, TimeInput } from "../../../components/inputs";
import { PhoneInput } from "../../../components/inputs";
import { OrderReferenceInput } from "../../../components/inputs/OrderReferenceInput";
import { OSFCUserSelectInput } from "../../../components/inputs/OSFCUserSelect";
import { PortInput } from "../../../components/inputs/PortInput";
import { SelectEnumInput } from "../../../components/inputs/SelectEnumInput";
import { SelectTextInput } from "../../../components/inputs/SelectTextInput";
import {
	SelectDisabledInput,
	SelectUppercaseInput,
} from "../../../components/inputs/SelectUppercaseInput";
import TerminalDatesInput from "../../../components/inputs/TerminalDatesInput";
import { YesNoInput } from "../../../components/inputs/YesNoInput";
import { useTerminalsContext } from "../../../contexts/TerminalsProvider";
import {
	DriverRoles,
	OrderNormalStatuses,
	OrderService,
	OrderStatus,
	OrderStatusCustomerEditOrder,
	OrderStatusTransporterEditOrder_1,
	OrderStatusTransporterEditOrder_2,
	UserRole,
	etdThirdAssigneeServices,
	perEligibleServices,
} from "../../../entities";
import HideableOrderField from "../../../entities/HideableOrderField";
import { LabelSource } from "../../../entities/LabelSource";
import { useTerminalFieldVisibility } from "../../../hooks/useTerminalFieldVisibility";
import { getNow } from "../../../utils/datetime";
import { getNowByTerminal } from "../../../utils/datetime";
import { validatePlaceLength } from "../../../utils/validation/order";
import { required } from "../../../utils/validation/required";
import { requiredIfNoFieldValue } from "../../../utils/validation/requiredIfNoFieldValue";
import { ColdStorageSection } from "./ColdStorageSection";
import { CustomFieldsBlock } from "./CustomFieldsBlock";
import { LoadingOrderInput } from "./inputs/LoadingOrderInput";
import { PrintedDocsInput } from "./inputs/PrintedDocsInput";
import { ServiceInput } from "./inputs/ServiceInput";
import { TransporterInput } from "./inputs/TransporterInput";
import { TripNumberInput } from "./inputs/TripNumberInput";
import { OrderProductArticlesBlock } from "./OrderProductArticlesBlock";
import { useXPartyChange } from "./shared";
import {
	DangerTextInputForInternalNotes,
	DimensionsBlock,
	ETAETDFormGroup,
	driversSelector,
	etaEtdPorts,
	useDriverChange,
	useEtaEtdSync,
} from "./shared";

const rowGroupStyle = {
	borderBottom: "2px solid #bdbdbd",
	paddingBottom: 4,
	marginBottom: 4,
};

export const ETAETDBlock = ({
	isAddMode,
	prefix,
	canEditOnlyTime,
	isFieldHidden,
	onIsDocForNfsaChange,
	...props
}) => {
	const { form } = useForm();
	const { values } = useFormState();
	const { data: terminals } = useTerminalsContext();
	const currentTerminal = terminals[values["terminal_id"]];
	const byTerminalFilter = { terminal_id: [values["terminal_id"]] };

	const externalTerminalFilter = { status: ExternalTerminalConnectionStatus.connected };
	const externalTerminalDisabled = prefix === "eta" || !currentTerminal;
	if (currentTerminal) {
		externalTerminalFilter["own_terminal_guid"] = [terminals[values["terminal_id"]].guid];
	}

	const { permissions } = usePermissions();
	const handleEtaEtdSync = useEtaEtdSync(prefix);
	const handleDriverChange = useDriverChange(prefix);
	const [handle4thPartyChangeOnTruck, handle4thPartyChangeOnTrailer, handleXPartyChangeOnDriver] =
		useXPartyChange(prefix);

	// const drivers = useSelector(driversSelector);

	if (!permissions) return null;

	const userId = permissions.id;
	const userCompanyId = permissions.company_id;

	if (
		!(
			isAddMode ||
			userCompanyId === values[`${prefix}_3rd_party`] ||
			userCompanyId === values[`${prefix}_4th_party`] ||
			(userCompanyId === values["company_id"] && userId === values[`${prefix}_driver_id`])
		)
	) {
		return <Grid item {...props} />;
	}

	// const companyId = values.company_id;
	// const isTimeSelected = !!values[`${prefix}_time`];
	const isManager =
		permissions.role === UserRole.transporter_manager ||
		permissions.role === UserRole.customer_and_transporter_manager;
	const isEmployee =
		permissions.role === UserRole.transporter_employee ||
		permissions.role === UserRole.customer_and_transporter_employee;
	const isDriver =
		permissions.role === UserRole.transporter_driver ||
		permissions.role === UserRole.customer_and_transporter_driver;
	const isTransporterSelected = !!values[`${prefix}_3rd_party`];
	const is4thPartySelected = !!values[`${prefix}_4th_party`];
	const isOwnCompany = values["company_id"] === userCompanyId;

	const is4thPartyUser = values[`${prefix}_4th_party`] === userCompanyId;

	const isDisabledTimeForEditing = isDriver
		? !((isTransporterSelected || isOwnCompany) && values[`${prefix}_driver_id`] === userId)
		: !(
				isTransporterSelected &&
				values[`${prefix}_3rd_party`] === userCompanyId &&
				values[`${prefix}_4th_party`] == null
		  );

	const isDisabledForUser = isDriver
		? !((is4thPartySelected || isOwnCompany) && values[`${prefix}_driver_id`] === userId)
		: !(is4thPartySelected && values[`${prefix}_4th_party`] === userCompanyId);

	const isDisabledForEditing = canEditOnlyTime || (isDisabledForUser && isDisabledTimeForEditing);
	const isColdStorageService = values.service === OrderService.coldStorage;
	const isIntoPlukkStorage = values.service === OrderService.intoPlukkStorage;

	const dateRequiredValueKeyPrefix = prefix === "eta" ? "etd" : "eta";
	const portKey = etaEtdPorts[prefix];
	const currentTransportFilter = { no_range: true, is_deleted: false };
	const currentTransportSort = { field: "name", order: "ASC" };
	if (isManager) {
		// const currentDriver = drivers[values[`${prefix}_driver_id`]];
		// if (currentDriver !== undefined) {
		// 	currentTransportFilter.company_id = drivers[values[`${prefix}_driver_id`]].company_id;
		// };
		currentTransportFilter.company_id = userCompanyId;
	}

	// const currentTransporter = values[`${prefix}_3rd_party`];
	const currentDriverFilter = {
		// company_id: currentTransporter,
		no_range: true,
		company_id: userCompanyId,
		is_blocked: false,
		is_deleted: false,
	};
	const currentDriverSort = { field: "first_name", order: "ASC" };
	const placePrefix = prefix === "eta" ? "from" : "to";

	const handle4thPartyChange = (e) => {
		handle4thPartyChangeOnTruck(e);
		handle4thPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
	};

	return (
		<ETAETDFormGroup {...props} prefix={prefix}>
			{/* Row 1: 4th party, Date, Time, Slot time, Port */}
			<FormGroupGrid style={rowGroupStyle}>
				{!is4thPartyUser && (
					<GridInput
						xs={12}
						sm={5}
						md={5}
						component={TransporterInput}
						source={`${prefix}_4th_party`}
						prefix={prefix}
						excludeIds={[values.company_id, values[`${prefix}_3rd_party`]]}
						disabled={!values.company_id}
						onChange={handle4thPartyChange}
						hidden={isFieldHidden(`${prefix}_4th_party`)}
					/>
				)}

				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source={`${prefix}_date`}
						disabled={isDisabledForEditing}
						validate={[
							isIntoPlukkStorage
								? requiredIfNoFieldValue(`${dateRequiredValueKeyPrefix}_date`)
								: required(),
						]}
						onChange={handleEtaEtdSync}
					/>
				</GridInput>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={TimeInput}
						source={`${prefix}_time`}
						disabled={isDisabledTimeForEditing}
					/>
				</GridInput>
				<GridInput xs={6} sm={3} component={TextInput} source={`${prefix}_slot_time`} disabled />
				<GridInput
					xs={6}
					sm={3}
					component={PortInput}
					source={portKey}
					filter={byTerminalFilter}
					disabled
				/>
			</FormGroupGrid>

			{/* Row 2: Place, Seal, Seal number, Trip number, Loading order */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					md={4}
					isDefaultTextMode
					disabled={isDisabledForEditing}
					component={SelectTextInput}
					sourceText={`place_${placePrefix}`}
					sourceSelect={`${prefix}_external_terminal_id`}
					reference="external-terminal-links"
					optionText="external_name"
					filter={externalTerminalFilter}
					sort={{ field: "external_name", order: "ASC" }}
					validate={[validatePlaceLength]}
					disabledSelect={externalTerminalDisabled}
					allowEmpty
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={12}
					md={4}
					disabled={isDisabledForEditing}
					component={CountryISOCodeInput}
					source={`place_${placePrefix}_country_code`}
					hidden={isFieldHidden(`place_${placePrefix}_country_code`)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={YesNoInput}
					source={prefix === "eta" ? "seal" : "plumb"}
					defaultValue={false}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL : HideableOrderField.PLUMB)}
				/>
				<GridInput
					xs={6}
					md={3}
					component={TextInput}
					source={prefix === "eta" ? "seal_number" : "plumb_number"}
					resettable
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL_NUMBER : HideableOrderField.PLUMB_NUMBER)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={TripNumberInput}
					source={prefix === "eta" ? "incoming_trip_number" : "outgoing_trip_number"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.INCOMING_TRIP_NUMBER : HideableOrderField.OUTGOING_TRIP_NUMBER)}
				/>
				<GridInput
					xs={6}
					md={2}
					component={LoadingOrderInput}
					source={prefix === "eta" ? "unloading_sequence" : "loading_sequence"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.UNLOADING_SEQUENCE : HideableOrderField.LOADING_SEQUENCE)}
				/>
			</FormGroupGrid>

			{/* Row 3: Truck, Trailer, Driver, Phone */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					md={6}
					lg={3}
					xl={3}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_truck"}
					sourceText={`${prefix}_truck`}
					sourceSelect={`${prefix}_truck_id`}
					prefix={prefix}
					reference="trucks"
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					textProps={{ inputProps: { style: { textTransform: "uppercase" } } }}
					hidden={isFieldHidden(`${prefix}_truck`)}
				/>
				<GridInput
					xs={12}
					md={6}
					lg={3}
					xl={3}
					component={SelectTextInput}
					label={"resources.orders.fields." + prefix + "_trailer"}
					sourceText={`${prefix}_trailer`}
					sourceSelect={`${prefix}_trailer_id`}
					reference="trailers"
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					textProps={{ inputProps: { style: { textTransform: "uppercase" } } }}
					hidden={isFieldHidden(`${prefix}_trailer`)}
				/>

				{(isManager || isEmployee) && (
					<GridInput
						xs={12}
						md={6}
						lg={3}
						xl={3}
						component={SelectTextInput}
						sourceText={`${prefix}_driver`}
						sourceSelect={`${prefix}_driver_id`}
						reference="drivers"
						optionText={(record) =>
							`${record.first_name} ${record.last_name} | ${formatPhoneNumber(record.phone)}`
						}
						filter={currentDriverFilter}
						sort={currentDriverSort}
						disabled={isDisabledForEditing}
						validate={[maxLength(64)]}
						onChange={handleDriverChange}
						hidden={isFieldHidden(`${prefix}_driver`)}
					/>
				)}
				{!isDisabledForEditing && (
					<GridInput
						xs={12}
						md={6}
						lg={3}
						xl={3}
						component={PhoneInput}
						source={`${prefix}_driver_phone`}
						disabled={isDisabledForEditing}
						hidden={isFieldHidden(`${prefix}_driver_phone`)}
					/>
				)}
				{prefix === "etd" && (
					<GridInput
						xs={6}
						sm={3}
						lg={2}
						xl={1}
						component={YesNoInput}
						source="is_doc_for_nfsa"
						defaultValue={false}
						onChange={onIsDocForNfsaChange}
						hidden={isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA)}
					/>
				)}
			</FormGroupGrid>

			{/* Row 4: Goods & Handling (ETA) or Assignee (ETD) */}
			<FormGroupGrid>
				{prefix === "eta" ? (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							hidden={
								values.service === OrderService.reloadCarCar ||
								isFieldHidden(HideableOrderField.COOLER_TYPE_ID)
							}
							disabled
						>
							<CoolerTypeSelect filter={{ order_service: values.service }} disabled uppercase />
						</GridInput>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="pallets"
							min={0}
							max={100}
							step={10}
							disabled
							hidden={isFieldHidden(HideableOrderField.PALLETS)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="boxes"
							min={0}
							max={100}
							step={5}
							disabled
							hidden={isFieldHidden(HideableOrderField.BOXES)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							source="kilos"
							min={0}
							max={1000}
							step={100}
							disabled
							hidden={isFieldHidden(HideableOrderField.KILOS)}
						/>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={CommodityInput}
							source="commodity_id"
							disabled
							hidden={isFieldHidden(HideableOrderField.COMMODITY_ID)}
						/>
						{!isColdStorageService && (
							<GridInput
								xs={6}
								sm={4}
								md={3}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_1"
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
							/>
						)}
					</>
				) : (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={OSFCUserSelectInput}
							source="assignee_2"
							hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
						/>
						{etdThirdAssigneeServices.includes(values.service) && (
							<GridInput
								xs={6}
								sm={4}
								md={3}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_3"
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
							/>
						)}
					</>
				)}
			</FormGroupGrid>

			{/* Row 5: All assignees for Cold Storage (ETA only) */}
			{isColdStorageService && prefix === "eta" && (
				<FormGroupGrid>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_1"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_2"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_3"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
					/>
				</FormGroupGrid>
			)}
		</ETAETDFormGroup>
	);
};

export const FormTransporter = ({ isAddMode, ...props }) => {
	const form = useForm();

	const { permissions } = usePermissions();
	const { values } = useFormState();
	const translate = useTranslate();
	const dataProvider = useDataProvider();
	const [canEditOnlyTime, setCanEditOnlyTime] = useState(false);
	const [customFields, setCustomFields] = useState([]);
	const { isFieldHidden } = useTerminalFieldVisibility(values?.terminal_id);
	const { data: terminals } = useTerminalsContext();
	const terminalId = values.terminal_id;
	useEffect(() => {
		if (permissions && DriverRoles.indexOf(permissions.role) !== -1) {
			dataProvider.getOne("drivers", { id: permissions.id }).then((result) => {
				setCanEditOnlyTime(result.data.can_edit_only_time);
			});
		}
		if (props.record && props.record?.custom_fields) {
			setCustomFields(props.record.custom_fields);
		}
	}, [permissions, props.record]);

	if (!permissions) return null;

	const status = props.record.status;
	const isColdStorageService = values.service === OrderService.coldStorage;

	const isDriver =
		permissions.role === UserRole.transporter_driver ||
		permissions.role === UserRole.customer_and_transporter_driver;
	let enabledFields;
	if (!isAddMode) {
		if (props.record.deleted_at) {
			enabledFields = {};
		} else if (OrderStatusTransporterEditOrder_2.indexOf(status) !== -1) {
			// no actions
		} else if (status === OrderStatus.ORDER_COMPLETED || status === OrderStatus.DELETED) {
			if (!isDriver) {
				enabledFields = {};
			} else {
				const orderCompletedAt = new Date(values["completed_at"] + "Z");
				const datetimeNow = getNowByTerminal(terminals[terminalId]);
				if (datetimeNow > addHoursToDate(orderCompletedAt, 48)) {
					enabledFields = {};
				}
			}
		} else {
			enabledFields = { notes: true, internal_notes: true };
		}
	}
	// const handleLabelSourceChange = (event) =>{
	// 	const val = event.target.value
	// 	if(val == LabelSource.pallets){
	// 		form.change("label", values.pallets)
	// 	} else if(val == LabelSource.boxes){
	// 		form.change("label", values.boxes)
	// 	}
	// }

	let tripNoAndLoadOrderDisable = true;
	if (isAddMode || OrderStatusTransporterEditOrder_1.indexOf(status) !== -1) {
		if (!isInArray(DriverRoles, permissions.role) || !canEditOnlyTime) {
			if (permissions.company_id === values.etd_4th_party && values.etd_4th_party) {
				tripNoAndLoadOrderDisable = false;
			} else if (permissions.company_id === values.etd_3rd_party && !values.etd_4th_party) {
				tripNoAndLoadOrderDisable = false;
			}
		}
	}
	const isStatusDraft = values.status === OrderStatus.DRAFT;
	const isOrderDraft = props?.record ? props.record.status === OrderStatus.DRAFT : isStatusDraft;
	const isOrderProductArticleVisible = isColdStorageService && !isStatusDraft && !isAddMode;

	return (
		<GridForm {...props} enabledFields={enabledFields}>
			{/* TODO: temporary set for Transporter edit partial update, If it ok we can set it for all edits */}
			<HiddenInput source="isUpdatePartial" defaultValue={1} />
			<FormGroupGrid>
				<GridInput
					source="company_id"
					sm={2}
					component={CompanySelect}
					filter={{ no_transporters: true }}
					disabled
				/>
				<GridInput
					source="reference"
					sm={2}
					component={OrderReferenceInput}
					validate={[required()]}
					disabled
				/>
				<GridInput
					source="service"
					sm={2}
					component={ServiceInput}
					validate={[required()]}
					defaultValue={OrderService.reloadCarCar}
					disabled
					isCouldStorageVisible={isAddMode || isColdStorageService}
				/>
				<GridInput sm={3} disabled>
					<ReferenceInput source="status" reference="order-statuses" validate={[required()]}>
						<SelectDisabledInput allowedIds={OrderNormalStatuses} optionText="name" />
					</ReferenceInput>
				</GridInput>
				<GridInput sm={3} component={YesNoInput} source="priority" defaultValue={false} disabled />
				<GridInput
					sm={1}
					component={YesNoInput}
					source="is_per"
					defaultValue={false}
					disabled
					hidden={
						isFieldHidden(HideableOrderField.IS_PER) ||
						!perEligibleServices.includes(values.service)
					}
				/>
				{/* <GridInput sm={2} component={LoadNoInput} source="load_no" disabled /> */}
			</FormGroupGrid>

			<FormGroupGrid xs={12}>
				<ETAETDBlock
					sm={12}
					md={12}
					lg={6}
					prefix="eta"
					canEditOnlyTime={canEditOnlyTime}
					{...props}
					enabledFields={enabledFields}
					isFieldHidden={isFieldHidden}
				/>
				{!isColdStorageService ? (
					<ETAETDBlock
						sm={12}
						md={12}
						lg={6}
						prefix="etd"
						canEditOnlyTime={canEditOnlyTime}
						{...props}
						enabledFields={enabledFields}
						isFieldHidden={isFieldHidden}
					/>
				) : (
					<ColdStorageSection sm={12} md={12} lg={6} isAddMode={isAddMode} {...props} />
				)}
			</FormGroupGrid>

			{/* <FormGroupGrid>
				<GridInput sm={3} component={YesNoInput} source="split_load" defaultValue={false} disabled />
				<GridInput sm={3} component={TripNumberInput} source="outgoing_trip_number" disabled />
				<GridInput sm={3} component={LoadingOrderInput} source="loading_sequence" disabled />
			</FormGroupGrid>

			<FormGroupGrid>
				<GridInput sm={3} hidden={values.service === OrderService.reloadCarCar}>
					<CoolerTypeSelect filter={{ order_service: values.service }} disabled uppercase />
				</GridInput>
				<GridInput xs={6} sm={3} component={YesNoInput} source="plumb" defaultValue={false} disabled />
				<GridInput xs={6} sm={3} component={YesNoInput} source="seal" defaultValue={false} disabled />
				<GridInput xs={6} sm={3} component={YesNoInput} source="printed_doc" defaultValue={false} disabled />
				<GridInput sm={3} component={CommodityInput} source="commodity" disabled />
			</FormGroupGrid> */}

			{/* <Grid item xs={12} md={6} lg={4}> */}
			{!isColdStorageService && (
				<FormGroupGrid
					xs={12}
					md={6}
					lg={4}
					title={translate("resources.orders.block-label.split-loads")}
					{...props}
					hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
				>
					<GridInput
						xs={4}
						component={YesNoInput}
						source="split_load"
						defaultValue={false}
						disabled
						hidden={isFieldHidden(HideableOrderField.SPLIT_LOAD)}
					/>
				</FormGroupGrid>
			)}
			{isOrderProductArticleVisible && !isOrderDraft && (
				<OrderProductArticlesBlock
					orderId={props.record.id}
					pallets={props.record.pallets}
					companyId={props.record.company_id}
					status={props.record.status}
					totalBoxes={props.record.total_boxes}
				/>
			)}
			{/* <DimensionsBlock {...props} disabled /> */}

			<FormGroupGrid>
				<GridInput
					sm={12}
					md={6}
					component={TextInput}
					source="notes"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
				/>
				<GridInput
					sm={12}
					md={6}
					className="red-border"
					component={DangerTextInputForInternalNotes}
					source="internal_notes"
					multiline
					rows={6}
					validate={[maxLength(500)]}
				/>
			</FormGroupGrid>
			<CustomFieldsBlock customFields={customFields} disabled={true} />
		</GridForm>
	);
};
